import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { CheckCircle2, Package, Truck, MessageCircle, Mail } from 'lucide-react';
import api from '@/lib/api';
import { inr } from '@/lib/format';
import Loader from '@/components/common/Loader';

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(location.state?.order || null);

  useEffect(() => {
    if (!order) api.get(`/orders/track/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderNumber]);

  if (!order) return <Loader label="Fetching your order" />;

  return (
    <div className="container-gaze py-10">
      <div className="mx-auto max-w-2xl">
        <div className="rounded-3xl border border-border bg-white p-8 text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100"><CheckCircle2 size={34} className="text-green-600" /></div>
          <h1 className="mt-4 text-3xl font-semibold">Thank you for your order!</h1>
          <p className="mt-2 text-sm text-muted-foreground">Your order <span className="font-semibold text-foreground" data-testid="order-number">{order.order_number}</span> has been confirmed.</p>
          <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MessageCircle size={14} className="text-green-600" /> WhatsApp confirmation sent</span>
            <span className="inline-flex items-center gap-1"><Mail size={14} /> Email receipt sent</span>
          </div>

          <div className="mt-6 flex items-center justify-between rounded-2xl bg-[hsl(var(--gaze-ivory))] p-4 text-left">
            <div className="flex items-center gap-2"><Package size={18} className="text-[hsl(var(--primary))]" /><span className="text-sm font-medium">Order confirmed</span></div>
            <div className="flex items-center gap-2 opacity-50"><Truck size={18} /><span className="text-sm">Estimated 3-7 days</span></div>
          </div>

          <div className="mt-6 space-y-3 text-left">
            {order.items.map((it) => (
              <div key={it.product_id + (it.name)} className="flex items-center gap-3">
                <img src={it.image} alt={it.name} className="h-14 w-14 rounded-lg object-cover" />
                <div className="flex-1"><p className="text-sm font-medium">{it.name}</p><p className="text-xs text-muted-foreground">Qty {it.qty}</p></div>
                <span className="text-sm font-semibold">{inr(it.line_total)}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-1 border-t border-border pt-4 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-green-700"><span>Discount</span><span>-{inr(order.discount)}</span></div>}
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{order.shipping === 0 ? 'Free' : inr(order.shipping)}</span></div>
            {order.gift_wrap_fee > 0 && <div className="flex justify-between" data-testid="order-success-gift-wrap-line"><span className="text-muted-foreground">Gift wrap</span><span>{inr(order.gift_wrap_fee)}</span></div>}
            {order.cod_fee > 0 && <div className="flex justify-between" data-testid="order-success-cod-fee-line"><span className="text-muted-foreground">COD fee</span><span>{inr(order.cod_fee)}</span></div>}
            <div className="flex justify-between text-base font-bold"><span>Total ({order.payment_method.toUpperCase()})</span><span>{inr(order.total)}</span></div>
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link to={`/track/${order.order_number}`} className="flex-1 rounded-full border border-[hsl(var(--primary))] py-3 text-sm font-semibold text-[hsl(var(--primary))]">Track Order</Link>
            <Link to="/shop" className="flex-1 rounded-full bg-[hsl(var(--primary))] py-3 text-sm font-semibold text-white">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
